const REQUEST_TIMEOUT = 1000
const MAX_COOKIE_NAME_LENGTH = 100

declare global {
  interface Window {
    gtag?: {
      (
        command: "get",
        id: string,
        field: "client_id",
        callback: (id: string) => void,
      ): void
      (...args: any[]): void
    }
    ym?: {
      (counterId: number, method: "getClientID", callback: (id: string) => void): void
      (counterId: number, method: "hit", url: string, options?: object): void
    }
  }
}
// === Вспомогательные ===
function getFromCookie(name: string): string | null {
  if (typeof document === "undefined") return null

  // Защита от ReDoS: валидация имени cookie
  if (!name || name.length > MAX_COOKIE_NAME_LENGTH) return null

  const escapedName = name.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")
  const regex = new RegExp(`(?:^|;\\s*)${escapedName}=([^;]*)`)
  const match = document.cookie.match(regex)

  if (!match) return null

  // Защита от ошибок декодирования
  try {
    return decodeURIComponent(match[1])
  } catch {
    return null
  }
}
function parseGAUniversal(value: string): string | null {
  const parts = value.split(".")
  return parts.length >= 4 && parts[2] && parts[3] ? `${parts[2]}.${parts[3]}` : null
}
async function withTimeout<T>(promise: Promise<T>): Promise<T | null> {
  let timeoutId: NodeJS.Timeout | undefined

  const timeout = new Promise<null>((resolve) => {
    timeoutId = setTimeout(() => resolve(null), REQUEST_TIMEOUT)
  })

  try {
    return await Promise.race([promise, timeout])
  } catch {
    return null
  } finally {
    // Защита от утечки таймера
    if (timeoutId !== undefined) {
      clearTimeout(timeoutId)
    }
  }
}
// === Google Analytics ===
async function getGA4ClientId(measurementId: string): Promise<string | null> {
  if (!window.gtag) return null
  const promise = new Promise<string | null>((resolve) => {
    try {
      window.gtag!("get", measurementId, "client_id", (id) => resolve(id || null))
    } catch {
      resolve(null)
    }
  })
  return withTimeout(promise)
}
export async function getGoogleClientID(): Promise<string | null> {
  if (typeof window === "undefined") return null
  try {
    const gaCookie = getFromCookie("_ga")
    if (gaCookie) return parseGAUniversal(gaCookie)
    const measurementId = process.env.NEXT_PUBLIC_GA_MEASUREMENT_ID
    if (measurementId?.startsWith("G-") && window.gtag) {
      return await getGA4ClientId(measurementId)
    }
    return null
  } catch (error) {
    console.error("Error getting Google Client ID:", error)
    return null
  }
}
// === Яндекс.Метрика ===
async function getYandexClientIdFromAPI(counterId: number): Promise<string | null> {
  if (!window.ym) return null
  const promise = new Promise<string | null>((resolve) => {
    try {
      window.ym!(counterId, "getClientID", (id) => resolve(id || null))
    } catch {
      resolve(null)
    }
  })
  return withTimeout(promise)
}
export const YANDEX_COUNTER_ID = (() => {
  const id = process.env.NEXT_PUBLIC_YANDEX_METRIKA_ID
  if (!id) return null
  const num = parseInt(id, 10)
  return isNaN(num) ? null : num
})()
export async function getYandexClientID(): Promise<string | null> {
  if (typeof window === "undefined") return null
  if (!YANDEX_COUNTER_ID) {
    if (process.env.NODE_ENV === "development") {
      console.warn("Yandex Metrika counter ID not configured")
    }
    return null
  }
  try {
    const ymUid = getFromCookie("_ym_uid")
    if (ymUid) return ymUid
    if (window.ym) {
      return await getYandexClientIdFromAPI(YANDEX_COUNTER_ID)
    }
    return null
  } catch (error) {
    console.error("Error getting Yandex Client ID:", error)
    return null
  }
}
